import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MediaUpload } from "@/components/site/MediaUpload";
import { toast } from "sonner";

export default function AdminHomepage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { data: courses } = useQuery({ queryKey: ["admin-courses"], queryFn: async () => (await api.get("/admin/courses")).data });

  useEffect(() => { api.get("/admin/homepage").then(({ data }) => setForm({ promo_banners: [], featured_course_ids: [], ...data })); }, []);
  if (!form) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/homepage", form);
      await api.post("/admin/homepage/publish");
      toast.success("Homepage saved & published to live site");
    }
    catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const toggleFeatured = (id) => {
    const ids = form.featured_course_ids || [];
    set("featured_course_ids", ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const banners = form.promo_banners || [];
  const setBanner = (i, k, v) => { const b = [...banners]; b[i] = { ...b[i], [k]: v }; set("promo_banners", b); };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Homepage</h1>
        <Button data-testid="save-homepage-btn" onClick={save} disabled={saving} className="bg-[#DCA846] text-black hover:bg-[#C5963B]">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save</>}
        </Button>
      </div>

      <div className="mt-8 space-y-6">
        <Section title="Hero">
          <F label="Headline"><Input data-testid="hero-headline" value={form.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
          <F label="Description"><Textarea rows={3} value={form.hero_description || ""} onChange={(e) => set("hero_description", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
          <MediaUpload testid="hero-video-upload" label="Hero 9:16 Video" value={form.hero_video} onChange={(v) => set("hero_video", v)} kind="video" accept="video/*" />
          <p className="-mt-2 text-xs text-zinc-500">Best: from Cloudflare Stream copy the full <b>HLS Manifest URL</b> (…/manifest/video.m3u8) or the <b>iframe embed URL</b> (…cloudflarestream.com/&lt;id&gt;/iframe) — these include your account subdomain. A bare Video UID only works on legacy accounts. Also accepts a direct .mp4/.webm/.mov URL or upload. The Cloudflare video must have "Require signed URLs" OFF for a public homepage.</p>
          <MediaUpload label="Hero Fallback Image" value={form.hero_image} onChange={(v) => set("hero_image", v)} kind="image" />
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Primary CTA Text"><Input value={form.cta_primary_text || ""} onChange={(e) => set("cta_primary_text", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
            <F label="Primary CTA Link"><Input value={form.cta_primary_link || ""} onChange={(e) => set("cta_primary_link", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
            <F label="Secondary CTA Text"><Input value={form.cta_secondary_text || ""} onChange={(e) => set("cta_secondary_text", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
            <F label="Secondary CTA Link"><Input value={form.cta_secondary_link || ""} onChange={(e) => set("cta_secondary_link", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></F>
          </div>
        </Section>

        <Section title="Featured Courses">
          <p className="text-sm text-zinc-500">Select which courses appear in the homepage Featured carousel.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {(courses || []).map((c) => (
              <label key={c.id} className="flex items-center gap-2 rounded border border-white/10 bg-zinc-900/40 px-3 py-2 text-sm">
                <input type="checkbox" checked={(form.featured_course_ids || []).includes(c.id)} onChange={() => toggleFeatured(c.id)} className="accent-[#DCA846]" />
                {c.title}
              </label>
            ))}
          </div>
        </Section>

        <Section title="Promo Banners">
          {banners.map((b, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Title" value={b.title || ""} onChange={(e) => setBanner(i, "title", e.target.value)} className="bg-zinc-900 border-white/10 text-white" />
              <Input placeholder="Text" value={b.text || ""} onChange={(e) => setBanner(i, "text", e.target.value)} className="bg-zinc-900 border-white/10 text-white" />
              <Button variant="ghost" onClick={() => set("promo_banners", banners.filter((_, x) => x !== i))} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => set("promo_banners", [...banners, { title: "", text: "" }])} className="border-white/15 text-white"><Plus className="mr-1 h-3 w-3" /> Add Banner</Button>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="rounded-lg border border-white/10 bg-zinc-900/30 p-5"><h2 className="mb-4 font-display text-lg uppercase tracking-wide text-[#DCA846]">{title}</h2><div className="space-y-4">{children}</div></div>;
}
function F({ label, children }) { return <div><Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label><div className="mt-1">{children}</div></div>; }
